import { combineReducers, configureStore } from "@reduxjs/toolkit";
import assetReducer from "./slices/AssetsSlice";
import animationLibraryReducer from "./slices/AnimationsSlice";
import authenticationReducer from "./slices/AuthenticationSlice";
import environmentReducer from "./slices/EnvironmentSlice";
import environmentPropReducer from "./slices/EnvironmentPropSlice";
import globalSlice from "./slices/GlobalSlice";
import historyReducer, {
  redo as redoAction,
  restoreHistoryFrame as restoreHistoryFrameAction,
  undo as undoAction,
} from "./slices/HistorySlice";
import interactionPickerReducer from "./slices/InteractionPickerSlice";
import onboardingReducer from "./slices/OnboardingSlice";
import metadataReducer from "./slices/MetadataSlice";
import projectsReducer from "./slices/ProjectsSlice";
import propLibraryReducer from "./slices/PropLibrarySlice";
import searchResultsSlice from "./slices/SearchResultsSlice";
import trainingLocalizationReducer from "./slices/TrainingLocalizationSlice";
import trainingReducer from "./slices/TrainingSlice";
import userReducer from "./slices/UserSlice";
import userSettingsReducer from "./slices/UserSettingsSlice";
import situationReducer from "./slices/SituationSlice";
import validationReducer from "./slices/ValidationSlice";
import characterPartLibraryReducer from "./slices/CharacterPartsSlice";
import websocketReducer from "./slices/WebSocketSlice";
import { createHistoryMiddleware } from "./historyMiddleware";
import { persistenceListenerMiddleware } from "./persistenceListenerMiddleware";
import { documentSnapshotsEqual } from "./documentState";
import { restoreHistoryFrame } from "./historyState";

const reducers = {
  authentication: authenticationReducer,
  animationLibrary: animationLibraryReducer,
  user: userReducer,
  userSettings: userSettingsReducer,
  projects: projectsReducer,
  assets: assetReducer,
  training: trainingReducer,
  trainingLocalization: trainingLocalizationReducer,
  history: historyReducer,
  interactionPicker: interactionPickerReducer,
  global: globalSlice,
  environment: environmentReducer,
  environmentProps: environmentPropReducer,
  metadata: metadataReducer,
  onboarding: onboardingReducer,
  propLibrary: propLibraryReducer,
  searchResults: searchResultsSlice,
  situations: situationReducer,
  validation: validationReducer,
  characterPartLibrary: characterPartLibraryReducer,
  websocket: websocketReducer,
};

const combinedReducer = combineReducers(reducers);

export function rootReducer(
  state: ReturnType<typeof combinedReducer> | undefined,
  action: { type: string }
): ReturnType<typeof combinedReducer> {
  if (state === undefined) {
    return combinedReducer(state, action);
  }
  if (action.type === restoreHistoryFrameAction.type) {
    const frame = (action as ReturnType<typeof restoreHistoryFrameAction>)
      .payload;
    const restored = restoreHistoryFrame(state, frame);
    return {
      ...state,
      ...restored,
      history: {
        ...state.history,
        documentDirty:
          state.history.savedCheckpoint === null ||
          !documentSnapshotsEqual(
            frame.document,
            state.history.savedCheckpoint
          ),
      },
    };
  }
  if (action.type === undoAction.type) {
    if (state.history.transactionActive || state.history.past.length === 0) {
      return state;
    }
    const entry = state.history.past[state.history.past.length - 1];
    const restored = restoreHistoryFrame(state, entry.before);
    return {
      ...state,
      ...restored,
      history: {
        ...state.history,
        past: state.history.past.slice(0, -1),
        future: [entry, ...state.history.future],
        documentDirty:
          state.history.savedCheckpoint === null ||
          !documentSnapshotsEqual(
            entry.before.document,
            state.history.savedCheckpoint
          ),
      },
    };
  }
  if (action.type === redoAction.type) {
    if (state.history.transactionActive || state.history.future.length === 0) {
      return state;
    }
    const entry = state.history.future[0];
    const restored = restoreHistoryFrame(state, entry.after);
    return {
      ...state,
      ...restored,
      history: {
        ...state.history,
        past: [...state.history.past, entry],
        future: state.history.future.slice(1),
        documentDirty:
          state.history.savedCheckpoint === null ||
          !documentSnapshotsEqual(
            entry.after.document,
            state.history.savedCheckpoint
          ),
      },
    };
  }
  return combinedReducer(state, action);
}

export const createAppStore = () =>
  configureStore({
    reducer: rootReducer,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware()
        .prepend(persistenceListenerMiddleware.middleware)
        .concat(createHistoryMiddleware()),
  });

export const store = createAppStore();

export type RootState = ReturnType<typeof store.getState>;
